import invariant from 'invariant';
import { max, min } from 'lodash';
import nullthrows from 'nullthrows';
import { match } from 'ts-pattern';
import {
  getBaseCountersAbility,
  getEachTurnCardAbility,
} from './CardAbilities';
import {
  CardAffinity,
  CardType,
  assertCardType,
  getCardAffinitySymbolPlaceholder,
  getCardTypeSymbolPlaceholder,
  isCardAffinity,
  isCardType,
} from './CardTypes';
import { FAVOR_PER_TRIBUTE_TOKEN } from './GameConstants';
import { EventTypes, GameEventData, GameEventOfType } from './GameEvents';
import { GameComputedState, TributeDef } from './GameModel';
import { Resource, ResourceToDisplayName } from './Resources';
import { CardWithID, Context, computeBaseCounters } from './Rules';
import * as Utility from './Utility';

export abstract class TributeLogic<TPlanningState = any> {
  private _tokens: number;
  private _thresholds: [number, number, number] | null;

  constructor(card: TributeDef) {
    this._tokens = card.tokens;
    this._thresholds = card.value || null;
  }

  public get tokens(): number {
    return this._tokens;
  }
  public get thresholds(): [number, number, number] | null {
    return this._thresholds;
  }

  onGainCounterDelta(context: Context): Utility.CounterDelta {
    const delta = Utility.makeCounterDelta();

    delta.favor = FAVOR_PER_TRIBUTE_TOKEN * this.tokens;
    delta.tributeTokens += this.tokens;

    return delta;
  }

  planningState(gameState: GameComputedState): TPlanningState | null {
    return null;
  }

  abstract description(): string;
  abstract shouldGainCard(
    context: Context,
    params: {
      turnEvents: GameEventData[];
      gameState: GameComputedState;
      planningState: TPlanningState | null;
    },
  ): boolean;
}

export function getTributeLogic(card: TributeDef): TributeLogic | null {
  const def = TributeDefinitions[card.function];
  return def ? def(card) : null;
}

function getEventsOfType<T extends EventTypes>(
  events: GameEventData[],
  type: T,
): GameEventOfType<T>[] {
  return events.filter(
    (event): event is GameEventOfType<T> => event.type === type,
  );
}

// Base class for tributes of the form "Obtain a trade row card meeting condition X".
// Where condition X is solely a function of the trade row cards.
abstract class BaseObtainCardTributeLogic extends TributeLogic<string[]> {
  abstract getTargetCards(gameState: GameComputedState): CardWithID[];

  override planningState(gameState: GameComputedState) {
    const targetCards = this.getTargetCards(gameState);
    return targetCards.map((card) => card.id);
  }

  override shouldGainCard(
    context: Context,
    params: {
      turnEvents: GameEventData[];
      gameState: GameComputedState;
      planningState: string[] | null;
    },
  ): boolean {
    if (!params.planningState) {
      return false;
    }
    const purchaseCardEvent = getEventsOfType(
      params.turnEvents,
      EventTypes.PURCHASE_CARD,
    ).find((event) => event.payload.userID === context.player.userID);
    if (!purchaseCardEvent) {
      return false;
    }

    return params.planningState.includes(purchaseCardEvent.payload.cardID);
  }
}

type TributeLogicObtainCardType =
  | {
      type: 'affinity';
      cardAffinity: CardAffinity;
    }
  | {
      type: 'type';
      cardType: CardType;
    };
function parseTributeLogicObtainCardType(
  params: string,
): TributeLogicObtainCardType {
  if (isCardAffinity(params)) {
    return { type: 'affinity', cardAffinity: params };
  } else if (isCardType(params)) {
    return {
      type: 'type',
      cardType: params,
    };
  } else {
    invariant(false, 'invalid TributeLogicObtainCardType: %s', params);
  }
}
class ObtainCardTributeLogic extends TributeLogic<string[]> {
  private _cardType: TributeLogicObtainCardType;
  constructor(card: TributeDef) {
    super(card);

    this._cardType = parseTributeLogicObtainCardType(card.params);
  }

  override description(): string {
    return match(this._cardType)
      .with(
        { type: 'type' },
        (cardType) =>
          `Obtain a ${getCardTypeSymbolPlaceholder(cardType.cardType, true)} Trade Row Card.`,
      )
      .with(
        { type: 'affinity' },
        (cardType) =>
          `Obtain a ${cardType.cardAffinity === CardAffinity.Basic ? 'Basic' : `${getCardAffinitySymbolPlaceholder(cardType.cardAffinity, true)} Trade Row`} Card.`,
      )
      .exhaustive();
  }

  override planningState(gameState: GameComputedState) {
    return gameState.table.map((card) => card.id);
  }

  private _isCompleted(cardToGain: CardWithID, tableIDs: string[]): boolean {
    if (
      this._cardType.type === 'affinity' &&
      this._cardType.cardAffinity === CardAffinity.Basic
    ) {
      return cardToGain.affinity === this._cardType.cardAffinity;
    }
    if (!tableIDs.includes(cardToGain.id)) {
      return false;
    }
    return match(this._cardType)
      .with(
        { type: 'type' },
        (cardType) => cardToGain.type === cardType.cardType,
      )
      .with(
        { type: 'affinity' },
        (cardType) => cardToGain.affinity === cardType.cardAffinity,
      )
      .exhaustive();
  }

  override shouldGainCard(
    context: Context,
    params: {
      turnEvents: GameEventData[];
      gameState: GameComputedState;
      planningState: string[] | null;
    },
  ): boolean {
    if (!params.planningState) {
      return false;
    }
    const purchaseCardEvent = getEventsOfType(
      params.turnEvents,
      EventTypes.PURCHASE_CARD,
    ).find((event) => event.payload.userID === context.player.userID);
    if (!purchaseCardEvent) {
      return false;
    }
    const purchaseCard =
      params.gameState.cardsByID[purchaseCardEvent.payload.cardID];

    return this._isCompleted(purchaseCard, params.planningState);
  }
}

type TributeLogicHaveCardsType =
  | {
      type: 'affinity';
      cardAffinity: CardAffinity;
    }
  | {
      type: 'type';
      cardType: CardType;
    }
  | {
      type: 'single';
    };
function parseTributeLogicHaveCardsType(
  params: string,
): TributeLogicHaveCardsType {
  if (params === 'SingleType') {
    return { type: 'single' };
  } else if (isCardAffinity(params)) {
    return { type: 'affinity', cardAffinity: params };
  } else {
    return {
      type: 'type',
      cardType: assertCardType(params),
    };
  }
}
function formatTributeLogicHaveCardsType(
  cardType: TributeLogicHaveCardsType,
): string {
  return match(cardType)
    .with(
      { type: 'affinity' },
      (cardType) =>
        `${getCardAffinitySymbolPlaceholder(cardType.cardAffinity, true)} cards`,
    )
    .with(
      { type: 'type' },
      (cardType) =>
        `${getCardTypeSymbolPlaceholder(cardType.cardType, true)} cards`,
    )
    .with(
      { type: 'single' },
      () =>
        `cards of the same Deity ($ZEONA_LIGHT, $TYROS_LIGHT, or $DERION_LIGHT)`,
    )
    .exhaustive();
}
class HaveCardsTypeTributeLogic extends TributeLogic {
  private _cardType: TributeLogicHaveCardsType;

  constructor(card: TributeDef) {
    super(card);

    this._cardType = parseTributeLogicHaveCardsType(card.params);
  }

  override description(): string {
    return `Own X\n${formatTributeLogicHaveCardsType(this._cardType)}.`;
  }

  private _isCompleted(cards: CardWithID[], age: number): boolean {
    const count = match(this._cardType)
      .with({ type: 'affinity' }, ({ cardAffinity }) =>
        Utility.countIf(cards, (card) => card.affinity === cardAffinity),
      )
      .with({ type: 'type' }, ({ cardType }) =>
        Utility.countIf(cards, (card) => card.type === cardType),
      )
      .with({ type: 'single' }, () => {
        return Math.max(
          Utility.countIf(
            cards,
            (card) => card.affinity === CardAffinity.Derion,
          ),
          Utility.countIf(
            cards,
            (card) => card.affinity === CardAffinity.Tyros,
          ),
          Utility.countIf(
            cards,
            (card) => card.affinity === CardAffinity.Zeona,
          ),
        );
      })
      .exhaustive();

    return count >= this.thresholds![age - 1];
  }

  shouldGainCard(context: Context): boolean {
    return this._isCompleted(context.player.cards, context.age);
  }
}

class HaveSetsTributeLogic extends TributeLogic {
  override description(): string {
    return 'Have X Sets of a $DERION_LIGHT, $TYROS_LIGHT, and $ZEONA_LIGHT card.';
  }

  private _isCompleted(cards: CardWithID[], age: number): boolean {
    const count = Utility.countBoardSets(cards);
    return count >= this.thresholds![age - 1];
  }

  shouldGainCard(context: Context): boolean {
    return this._isCompleted(context.player.cards, context.age);
  }
}

type Direction = 'first' | 'last';
class ObtainDirectionTributeLogic extends BaseObtainCardTributeLogic {
  constructor(
    card: TributeDef,
    private _direction: Direction,
  ) {
    super(card);
    invariant(
      this._direction === 'first' || this._direction === 'last',
      'invalid direction: %s',
      this._direction,
    );
  }

  override description(): string {
    return `Obtain the ${this._direction} Trade Row Card.`;
  }

  override getTargetCards(gameState: GameComputedState): CardWithID[] {
    return match(this._direction)
      .with('first', () => [gameState.table[0]])
      .with('last', () => [gameState.table[gameState.table.length - 1]])
      .exhaustive();
  }
}

class ObtainMostExpensiveCardTributeLogic extends BaseObtainCardTributeLogic {
  override description(): string {
    return `Obtain a Trade Row Card with the highest Cost.`;
  }

  override getTargetCards(gameState: GameComputedState): CardWithID[] {
    const highestTotalCost = max(gameState.table.map((card) => card.cost))!;
    return gameState.table.filter((card) => card.cost >= highestTotalCost);
  }
}

class ObtainLeastExpensiveCardTributeLogic extends BaseObtainCardTributeLogic {
  override description(): string {
    return `Obtain a Trade Row Card with the lowest Cost.`;
  }

  override getTargetCards(gameState: GameComputedState): CardWithID[] {
    const lowestTotalCost = min(gameState.table.map((card) => card.cost))!;
    return gameState.table.filter((card) => card.cost <= lowestTotalCost);
  }
}

class ObtainMostValuableCardTributeLogic extends BaseObtainCardTributeLogic {
  override description(): string {
    return `Obtain a Trade Row Card with the highest Base Favor.`;
  }

  override getTargetCards(gameState: GameComputedState): CardWithID[] {
    const highestFavorValue = max(gameState.table.map((card) => card.favor))!;
    return gameState.table.filter((card) => card.favor >= highestFavorValue);
  }
}

class ObtainLeastValuableCardTributeLogic extends BaseObtainCardTributeLogic {
  override description(): string {
    return `Obtain a Trade Row Card with the lowest Base Favor.`;
  }

  override getTargetCards(gameState: GameComputedState): CardWithID[] {
    const lowestFavorValue = min(gameState.table.map((card) => card.favor))!;
    return gameState.table.filter((card) => card.favor <= lowestFavorValue);
  }
}

class ObtainSynergyCardTributeLogic extends BaseObtainCardTributeLogic {
  override description(): string {
    return `Obtain a Card with an Upgrade or Prophecy Effect.`;
  }

  override getTargetCards(gameState: GameComputedState): CardWithID[] {
    return gameState.table.filter((card) => {
      return (
        getBaseCountersAbility(card)?.outputs.some(
          (output) => !!output.upgradeCondition,
        ) ||
        getEachTurnCardAbility(card)?.outputs.some(
          (output) => !!output.upgradeCondition,
        ) ||
        !!card.endOfGameEffect
      );
    });
  }
}

class ClashWithOpponentWithLowestBidTributeLogic extends TributeLogic {
  override description(): string {
    return `Clash with an opponent with the lowest Bid.`;
  }

  override shouldGainCard(
    context: Context,
    params: { turnEvents: GameEventData[] },
  ): boolean {
    const allBidEvents = getEventsOfType(params.turnEvents, EventTypes.BID);
    if (allBidEvents.length === 0) {
      return false;
    }

    // Find the globally lowest bid
    const lowestBid = min(
      allBidEvents.map((event) => event.payload.bid.military),
    )!;

    // Get lowest bidders, excluding myself
    const lowestBidderIDs = allBidEvents
      .filter(
        (event) =>
          event.payload.bid.military === lowestBid &&
          event.payload.userID !== context.player.userID,
      )
      .map((event) => event.payload.userID);

    if (lowestBidderIDs.length === 0) {
      return false;
    }

    // Check if any lowest bidder was in my conflict
    const conflictResults = getEventsOfType(
      params.turnEvents,
      EventTypes.CONFLICT_RESULTS,
    ).find((event) =>
      Object.keys(event.payload.rounds[0]).includes(context.player.userID),
    );

    if (!conflictResults) {
      return false;
    }

    const clashPlayerIDs = Object.keys(conflictResults.payload.rounds[0]);
    return lowestBidderIDs.some((id) => clashPlayerIDs.includes(id));
  }
}

type ClashTributeLogicComparator =
  | 'most'
  | 'more'
  | 'equal'
  | 'fewer'
  | 'fewest';
type ClashTributeLogicCondition = {
  comparator: ClashTributeLogicComparator;
  comparedType:
    | { type: 'affinity'; cardAffinity: CardAffinity }
    | { type: 'type'; cardType: CardType }
    | { type: 'resource'; resource: Resource };
  // | { type: 'bid' };
};
class ClashWithOpponentMeetingConditionTributeLogic extends TributeLogic<{
  [playerID: string]: number;
}> {
  constructor(
    card: TributeDef,
    private _condition: ClashTributeLogicCondition,
  ) {
    super(card);
  }
  override description(): string {
    let comparator_string: Record<ClashTributeLogicComparator, string> = {
      most: 'the most',
      more: 'more',
      equal: 'the same number of',
      fewer: 'fewer',
      fewest: 'the fewest',
    };
    let compared_type_string: string = '';
    let suffix = '.';

    compared_type_string = match(this._condition)
      .with({ comparedType: { type: 'affinity' } }, (condition) => {
        return (
          getCardAffinitySymbolPlaceholder(
            condition.comparedType.cardAffinity,
            true,
          ) + ' cards'
        );
      })
      .with({ comparedType: { type: 'type' } }, (condition) => {
        return (
          getCardTypeSymbolPlaceholder(condition.comparedType.cardType, true) +
          ' cards'
        );
      })
      .with({ comparedType: { type: 'resource' } }, (condition) => {
        // XXX: address resources all being called base/tokens
        return (
          ResourceToDisplayName[condition.comparedType.resource] + ' Tokens'
        );
      })
      // .with({ comparedType: { type: 'bid' } }, () => {
      //   comparator_string = {
      //     most: 'the highest',
      //     more: 'a higher',
      //     equal: 'the same',
      //     fewer: 'a lower',
      //     fewest: 'the lowest',
      //   };
      //   return (compared_type_string = 'Bid');
      // })
      .exhaustive();

    return `Clash with a player with ${comparator_string[this._condition.comparator]} ${compared_type_string}${suffix}`;
  }

  playerIDsMeetingClashCondition(
    playerValue: number,
    playerValues: { [key: string]: number },
  ): string[] {
    const playerIDs = Object.keys(playerValues);
    return playerIDs.filter((playerID) => {
      const value = playerValues[playerID];
      return match(this._condition.comparator)
        .with('most', () => {
          return value >= Math.max(...Object.values(playerValues));
        })
        .with('more', () => {
          return value > playerValue;
        })
        .with('equal', () => {
          return value === playerValue;
        })
        .with('fewer', () => {
          return value < playerValue;
        })
        .with('fewest', () => {
          return value <= Math.min(...Object.values(playerValues));
        })
        .exhaustive();
    });
  }

  override planningState(gameState: GameComputedState) {
    return Object.fromEntries(
      gameState.playerStates.map((player) => [
        player.userID,
        match(this._condition.comparedType)
          .with({ type: 'affinity' }, (condition) => {
            return Utility.countIf(
              player.cards,
              (card) => card.affinity === condition.cardAffinity,
            );
          })
          .with({ type: 'type' }, (condition) => {
            return Utility.countIf(
              player.cards,
              (card) => card.type === condition.cardType,
            );
          })
          .with({ type: 'resource' }, (condition) => {
            return player.counters[condition.resource];
          })
          // .with({ type: 'bid' }, () => {
          //   return nullthrows(player.bid).military;
          // })
          .exhaustive(),
      ]),
    );
  }

  override shouldGainCard(
    context: Context,
    params: {
      turnEvents: GameEventData[];
      planningState: {
        [playerID: string]: number;
      } | null;
    },
  ): boolean {
    if (!params.planningState) {
      return false;
    }

    const conflictResults = getEventsOfType(
      params.turnEvents,
      EventTypes.CONFLICT_RESULTS,
    ).find((event) => event.payload.winningPlayerID === context.player.userID);

    if (!conflictResults) {
      return false;
    }
    const clashTargetPlayerIds = this.playerIDsMeetingClashCondition(
      params.planningState[context.player.userID],
      params.planningState,
    );
    const clashPlayerIDs = Object.keys(conflictResults.payload.rounds[0]);
    return clashPlayerIDs.some((playerID) =>
      clashTargetPlayerIds.includes(playerID),
    );
  }
}

class DefeatOpponentTributeLogic extends TributeLogic {
  override description(): string {
    return `Defeat an opponent in a conflict.`;
  }

  override shouldGainCard(
    context: Context,
    params: {
      turnEvents: GameEventData[];
    },
  ): boolean {
    const conflictResults = getEventsOfType(
      params.turnEvents,
      EventTypes.CONFLICT_RESULTS,
    ).find((event) => event.payload.winningPlayerID === context.player.userID);

    if (!conflictResults) {
      return false;
    }
    if (Object.keys(conflictResults.payload.rounds[0]).length === 1) {
      return false;
    }
    return true;
  }
}

class ClashWithOpponentTributeLogic extends TributeLogic {
  override description(): string {
    return `Clash with another player.`;
  }

  override shouldGainCard(
    context: Context,
    params: {
      turnEvents: GameEventData[];
    },
  ): boolean {
    const conflictResults = getEventsOfType(
      params.turnEvents,
      EventTypes.CONFLICT_RESULTS,
    ).find((event) => event.payload.winningPlayerID === context.player.userID);

    if (!conflictResults) {
      return false;
    }
    return (
      !!conflictResults.payload.rounds[0][context.player.userID] &&
      Object.keys(conflictResults.payload.rounds[0]).length > 1
    );
  }
}

class GainCardWithoutMilitaryTributeLogic extends TributeLogic {
  override description(): string {
    return `Obtain a Trade Row Card without spending any $MILITARY_WHITE_BORDER.`;
  }

  override planningState(gameState: GameComputedState): string[] {
    return gameState.table.map((card) => card.id);
  }

  override shouldGainCard(
    context: Context,
    params: {
      turnEvents: GameEventData[];
      gameState: GameComputedState;
      planningState: string[] | null;
    },
  ): boolean {
    if (!params.planningState) {
      return false;
    }
    const purchaseCardEvent = getEventsOfType(
      params.turnEvents,
      EventTypes.PURCHASE_CARD,
    ).find((event) => event.payload.userID === context.player.userID);
    if (!purchaseCardEvent) {
      return false;
    }

    if (!params.planningState.includes(purchaseCardEvent.payload.cardID)) {
      return false;
    }

    const bidEvent = getEventsOfType(params.turnEvents, EventTypes.BID).find(
      (event) => event.payload.userID === context.player.userID,
    );
    if (!bidEvent) {
      return false;
    }
    return bidEvent.payload.spentMilitary === 0;
  }
}

class ObtainCardWithCostLowerThanTownsTributeLogic extends TributeLogic {
  override description(): string {
    return `Obtain a Trade Row Card without spending any $GOLD.`;
  }

  override shouldGainCard(
    context: Context,
    params: {
      turnEvents: GameEventData[];
      gameState: GameComputedState;
      planningState: string[] | null;
    },
  ): boolean {
    const purchaseCardEvent = getEventsOfType(
      params.turnEvents,
      EventTypes.PURCHASE_CARD,
    ).find((event) => event.payload.userID === context.player.userID);
    if (!purchaseCardEvent) {
      return false;
    }
    return purchaseCardEvent.payload.spentCounters.gold === 0;
  }
}

class BidAllMilitaryTributeLogic extends TributeLogic {
  override description(): string {
    return `Bid all of your $MILITARY_WHITE_BORDER.`;
  }

  override planningState(gameState: GameComputedState): {
    [playerID: string]: number;
  } {
    return Object.fromEntries(
      gameState.playerStates.map((player) => [
        player.userID,
        player.counters.military,
      ]),
    );
  }

  override shouldGainCard(
    context: Context,
    params: {
      turnEvents: GameEventData[];
      gameState: GameComputedState;
      planningState: {
        [playerID: string]: number;
      } | null;
    },
  ): boolean {
    // Get the player's spendable military at planning time
    const spendableMilitaryAtPlanning =
      params.planningState?.[context.player.userID];
    if (spendableMilitaryAtPlanning === undefined) {
      return false;
    }

    // Get the actual military spent from the BID event
    const bidEvent = getEventsOfType(params.turnEvents, EventTypes.BID).find(
      (event) => event.payload.userID === context.player.userID,
    );
    if (!bidEvent) {
      return false;
    }

    // Player bid all their military if they spent all their spendable military
    return bidEvent.payload.spentMilitary === spendableMilitaryAtPlanning;
  }
}

class HaveCountersTributeLogic extends TributeLogic {
  private _counterType: Resource;
  private _includeBase: boolean;

  constructor(card: TributeDef, includeBase: boolean) {
    super(card);

    this._counterType = card.params as Resource;
    this._includeBase = includeBase;
  }

  override description(): string {
    return `Have\nX ${this._getDescriptionResourceText()}\nat the end of turn.`;
  }

  private _getDescriptionResourceText(): string {
    if (this._counterType === 'gold') {
      return this._includeBase ? '$TOWN' : '$GOLD';
    } else if (this._counterType === 'military') {
      return this._includeBase ? '$FORT' : '$MILITARY_WHITE_BORDER';
    }
    invariant(false, 'unimplemented support for resource ' + this._counterType);
  }

  private _isCompleted(context: Context): boolean {
    let counters = context.player.counters[this._counterType];
    if (this._includeBase) {
      const baseCounters = computeBaseCounters(context)[this._counterType];
      counters += baseCounters;
    }
    return counters >= this.thresholds![context.age - 1];
  }

  override shouldGainCard(context: Context): boolean {
    return this._isCompleted(context);
  }
}

// class GainResourceTributeLogic extends TributeLogic {
//   private _resource: Resource;

//   constructor(card: TributeDef) {
//     super(card);

//     this._resource = card.params as Resource;
//   }

// override description(): string {
//   if (this._resource === 'favor') {
//     return `Gain\nX $FAVOR from\n other sources.`;
//   }
//   return `Gain X ${getResourceSymbolPlaceholder(assertResource(this._resource))} tokens.`;
// }

//   override planningDataFunction(
//     context: Context,
//     extra: PlanningDataExtra,
//   ): TributeLogicPlanningData {
//     return context.production_delta[this._resource];
//   }

//   override resolutionDataFunction(
//     context: Context,
//     table: CardWithID[],
//     cardToGain: CardWithID,
//   ): TributeLogicResolutionData {
//     return context.production_delta[this._resource];
//   }

//   override productionDataFunction(context: Context): TributeLogicPlanningData {
//     return context.production_delta[this._resource];
//   }

//   shouldGainCard(
//     context: Context,
//     planning_data: TributeLogicPlanningData | null,
//     resolution_data: TributeLogicResolutionData | null,
//     production_data: TributeLogicProductionData | null,
//   ): boolean {
//     const total =
//       (planning_data || 0) +
//       (resolution_data || 0) +
//       (production_data || 0) +
//       context.production_delta[this._resource];
//     return total >= this.thresholds![context.age - 1];
//   }
// }

class CompleteOtherTributeLogic extends TributeLogic {
  description(): string {
    return `Complete another Tribute.`;
  }
  private _isAnotherTributeCompleted(context: Context): boolean {
    return context.completedTributeIDs.length > 0;
  }

  override shouldGainCard(context: Context): boolean {
    return this._isAnotherTributeCompleted(context);
  }
}

class ClashAgainstHigherBidOpponentTributeLogic extends TributeLogic {
  description(): string {
    return `Clash with an opponent who bid more total Military.`;
  }

  shouldGainCard(
    context: Context,
    params: {
      turnEvents: GameEventData[];
      gameState: GameComputedState;
    },
  ): boolean {
    const conflictResults = getEventsOfType(
      params.turnEvents,
      EventTypes.CONFLICT_RESULTS,
    ).find(
      (event) =>
        !!event.payload.rounds[0][context.player.userID] &&
        Object.keys(event.payload.rounds[0]).length > 1,
    );

    if (!conflictResults) {
      return false;
    }
    const firstRound = conflictResults.payload.rounds[0];
    let playerIDs = Object.keys(firstRound);
    const playerIDToBonus = Object.fromEntries(
      playerIDs.map((playerID) => [playerID, firstRound[playerID].bonus]),
    );

    const myBonus = playerIDToBonus[context.player.userID];
    if (myBonus === undefined) {
      return false;
    }

    const maxBonus = max(Object.values(playerIDToBonus))!;
    return myBonus < maxBonus;
  }
}

class HaveFewestCountersTributeLogic extends TributeLogic {
  private _counterType: Resource;

  constructor(card: TributeDef) {
    super(card);

    this._counterType = card.params as Resource;
  }

  description(): string {
    return `Have fewer ${ResourceToDisplayName[this._counterType]} tokens than all other players.`;
  }

  shouldGainCard(
    context: Context,
    params: {
      gameState: GameComputedState;
    },
  ): boolean {
    return !!params.gameState.playerStates.every((player) => {
      return (
        player.userID === context.player.userID ||
        player.counters[this._counterType] >
          context.player.counters[this._counterType]
      );
    });
  }
}

class BidMostTributeLogic extends TributeLogic {
  description(): string {
    return `Bid more than all other players.`;
  }

  shouldGainCard(
    context: Context,
    params: {
      gameState: GameComputedState;
    },
  ): boolean {
    if (!context.player.bid) {
      return false;
    }
    return params.gameState.playerStates.every((player) => {
      if (!player.bid) {
        return false;
      }
      return (
        player.userID === context.player.userID ||
        player.bid.military < nullthrows(context.player.bid).military
      );
    });
  }
}

export function getCurrentTurnEvents(
  gameState: GameComputedState,
): GameEventData[] {
  const turnEvents: GameEventData[] = [];
  for (let i = gameState.events.length - 1; i >= 0; i--) {
    const event = gameState.events[i];
    if (event.type === EventTypes.SET_UP_TURN) {
      break;
    }
    turnEvents.push(event);
  }
  turnEvents.reverse();
  return turnEvents;
}

const TributeDefinitions: Record<string, (card: TributeDef) => TributeLogic> = {
  ObtainCard: (card) => new ObtainCardTributeLogic(card),
  HaveCards: (card) => new HaveCardsTypeTributeLogic(card),
  HaveSets: (card) => new HaveSetsTributeLogic(card),
  HaveCounters: (card) => new HaveCountersTributeLogic(card, false),
  HaveCountersAndBase: (card) => new HaveCountersTributeLogic(card, true),
  ObtainFirst: (card) => new ObtainDirectionTributeLogic(card, 'first'),
  ObtainLast: (card) => new ObtainDirectionTributeLogic(card, 'last'),
  ObtainMostExpensive: (card) => new ObtainMostExpensiveCardTributeLogic(card),
  ObtainLeastExpensive: (card) =>
    new ObtainLeastExpensiveCardTributeLogic(card),
  ObtainMostValuable: (card) => new ObtainMostValuableCardTributeLogic(card),
  ObtainLeastValuable: (card) => new ObtainLeastValuableCardTributeLogic(card),
  ObtainSynergyCard: (card) => new ObtainSynergyCardTributeLogic(card),
  // DraftSameAsMostFavor: (card) =>
  //   new ClashWithOpponentMeetingConditionTributeLogic(card, {
  //     comparator: 'most',
  //     comparedType: { type: 'resource', resource: 'favor' },
  //   }),
  // DraftSameAsMoreFavor: (card) =>
  //   new ClashWithOpponentMeetingConditionTributeLogic(card, {
  //     comparator: 'more',
  //     comparedType: { type: 'resource', resource: 'favor' },
  //   }),
  ClashWithOpponentWithLowestBid: (card) =>
    new ClashWithOpponentWithLowestBidTributeLogic(card),
  ClashWithOpponentWithMostTributeTokens: (card) =>
    new ClashWithOpponentMeetingConditionTributeLogic(card, {
      comparator: 'most',
      comparedType: { type: 'resource', resource: 'tributeTokens' },
    }),
  ObtainCardWithCostLowerThanTowns: (card) =>
    new ObtainCardWithCostLowerThanTownsTributeLogic(card),
  DefeatOpponent: (card) => new DefeatOpponentTributeLogic(card),
  GainCardWithoutMilitary: (card) =>
    new GainCardWithoutMilitaryTributeLogic(card),
  // GainResource: (card) => new GainResourceTributeLogic(card),
  DraftAgainstHigherMilitary: (card) =>
    new ClashAgainstHigherBidOpponentTributeLogic(card),
  HaveFewestCounters: (card) => new HaveFewestCountersTributeLogic(card),
  BidMost: (card) => new BidMostTributeLogic(card),
  BidAllMilitary: (card) => new BidAllMilitaryTributeLogic(card),
  CompleteOtherTribute: (card) => new CompleteOtherTributeLogic(card),
  ClashWithOpponent: (card) => new ClashWithOpponentTributeLogic(card),
};
